/**
 * Full SkinPilot project snapshot: skin pixels, editor UI, undo stacks, chat.
 * Never includes API keys. Skin PNG encoding is lossless RGBA at its native size.
 */

import {
  buildChatHistoryDocument,
  parseChatHistoryJson,
  type ChatHistoryDocument,
  type ImportedChatMessage,
} from "../ai/chatHistory";
import {
  MAX_STORED_IMAGE_DATA_BYTES,
  measureUserImageDataBytes,
} from "../ai/imageAttachments";
import { createSkinDocument } from "../skin/document";
import { exportSkinPng, importSkinPng } from "../skin/png";
import type {
  RgbaHex,
  SkinDocument,
  SkinLayer,
  SkinModel,
  SurfaceRef,
} from "../skin/types";
import {
  deriveChatSessionTitle,
  MAX_CHAT_SESSION_TIMESTAMP,
  MAX_CHAT_SESSIONS,
  useChatSessionStore,
  type ChatSessionRecord,
} from "../store/chat-session-store";
import {
  useSkinStore,
  type BrushSize,
  type EditorMode,
  type EditorTool,
  type HistoryEntry,
} from "../store/skin-store";

export const PROJECT_FORMAT = "skinpilot-project" as const;
export const PROJECT_VERSION = 2 as const;
const LEGACY_PROJECT_VERSION = 1 as const;

export type ProjectEditorState = {
  selectedSurface: SurfaceRef;
  editorMode: EditorMode;
  tool: EditorTool;
  color: RgbaHex;
  recentColors: RgbaHex[];
  brushSize: BrushSize;
  activeLayer: SkinLayer;
  showInner: boolean;
  showOuter: boolean;
  mirror: boolean;
  zoom: number;
  pan: { x: number; y: number };
};

export type SerializedSkinDocument = {
  model: SkinModel;
  revision: number;
  /** Lossless native-size RGBA PNG as standard base64 (no data: prefix). */
  pngBase64: string;
};

export type SerializedHistoryEntry = {
  document: SerializedSkinDocument;
  source: "manual" | "ai" | "system";
  label?: string;
  chatSessionId?: string;
  rollbackOfChatSessionId?: string;
};

export type SerializedProjectChatSession = {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  history: ChatHistoryDocument;
};

export type ProjectBundle = {
  format: typeof PROJECT_FORMAT;
  version: typeof PROJECT_VERSION;
  exportedAt: string;
  skin: SerializedSkinDocument;
  editor: ProjectEditorState;
  history: {
    past: SerializedHistoryEntry[];
    future: SerializedHistoryEntry[];
  };
  chat: {
    activeSessionId: string;
    sessions: SerializedProjectChatSession[];
  };
};

export type ProjectSnapshot = {
  document: SkinDocument;
  editor: ProjectEditorState;
  history: HistoryEntry[];
  future: HistoryEntry[];
  chatSessions: ChatSessionRecord[];
  activeChatSessionId: string;
  chatMessages: ImportedChatMessage[];
  chatImagesWereStripped: boolean;
};

const MAX_PROJECT_BYTES = 32 * 1024 * 1024;
const MAX_HISTORY_ENTRIES = 100;
const EDITOR_TOOLS = [
  "pencil",
  "eraser",
  "eyedropper",
  "fill",
  "line",
  "rectangle",
] as const;
const BRUSH_SIZES = [1, 2, 4] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isValidTimestamp(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= MAX_CHAT_SESSION_TIMESTAMP
  );
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

export async function serializeSkinDocument(
  document: SkinDocument,
): Promise<SerializedSkinDocument> {
  const blob = await exportSkinPng(document);
  const bytes = new Uint8Array(await blob.arrayBuffer());
  return {
    model: document.model,
    revision: document.revision,
    pngBase64: bytesToBase64(bytes),
  };
}

export async function deserializeSkinDocument(
  serialized: SerializedSkinDocument,
): Promise<SkinDocument> {
  if (serialized.model !== "classic" && serialized.model !== "slim") {
    throw new Error("项目皮肤体型无效。");
  }
  if (!Number.isSafeInteger(serialized.revision) || serialized.revision < 0) {
    throw new Error("项目皮肤修订号无效。");
  }
  if (typeof serialized.pngBase64 !== "string" || !serialized.pngBase64) {
    throw new Error("项目缺少皮肤 PNG 数据。");
  }
  const bytes = base64ToBytes(serialized.pngBase64);
  const blob = new Blob([toArrayBuffer(bytes)], {
    type: "image/png",
  });
  const decoded = await importSkinPng(blob, serialized.model);
  return createSkinDocument(
    decoded.pixels,
    serialized.model,
    serialized.revision,
  );
}

async function serializeHistoryEntry(
  entry: HistoryEntry,
): Promise<SerializedHistoryEntry> {
  return {
    document: await serializeSkinDocument(entry.document),
    source: entry.source,
    label: entry.label,
    chatSessionId: entry.chatSessionId,
    rollbackOfChatSessionId: entry.rollbackOfChatSessionId,
  };
}

async function deserializeHistoryEntry(
  entry: unknown,
  label: string,
): Promise<HistoryEntry> {
  if (!isRecord(entry) || !isRecord(entry.document)) {
    throw new Error(`${label}历史条目无效。`);
  }
  const source = entry.source;
  if (source !== "manual" && source !== "ai" && source !== "system") {
    throw new Error(`${label}历史来源无效。`);
  }
  return {
    document: await deserializeSkinDocument(
      entry.document as SerializedSkinDocument,
    ),
    source,
    label: typeof entry.label === "string" ? entry.label : undefined,
    chatSessionId: readOptionalHistorySessionId(
      entry.chatSessionId,
      `${label}chatSessionId`,
    ),
    rollbackOfChatSessionId: readOptionalHistorySessionId(
      entry.rollbackOfChatSessionId,
      `${label}rollbackOfChatSessionId`,
    ),
  };
}

function readOptionalHistorySessionId(
  value: unknown,
  label: string,
): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "string" || !value.trim() || value.length > 200) {
    throw new Error(`${label} 无效。`);
  }
  return value;
}

function captureEditorState(): ProjectEditorState {
  const state = useSkinStore.getState();
  return {
    selectedSurface: { ...state.selectedSurface },
    editorMode: state.editorMode,
    tool: state.tool,
    color: state.color,
    recentColors: [...state.recentColors],
    brushSize: state.brushSize,
    activeLayer: state.activeLayer,
    showInner: state.showInner,
    showOuter: state.showOuter,
    mirror: state.mirror,
    zoom: state.zoom,
    pan: { ...state.pan },
  };
}

function parseEditorState(value: unknown): ProjectEditorState {
  if (!isRecord(value)) throw new Error("项目缺少编辑器状态。");

  const surface = value.selectedSurface;
  if (
    !isRecord(surface) ||
    typeof surface.part !== "string" ||
    typeof surface.layer !== "string" ||
    typeof surface.face !== "string"
  ) {
    throw new Error("项目选中表面无效。");
  }
  const parts = ["head", "body", "rightArm", "leftArm", "rightLeg", "leftLeg"];
  const faces = ["front", "back", "left", "right", "top", "bottom"];
  if (
    !parts.includes(surface.part) ||
    (surface.layer !== "inner" && surface.layer !== "outer") ||
    !faces.includes(surface.face)
  ) {
    throw new Error("项目选中表面枚举无效。");
  }

  const editorMode = value.editorMode;
  if (editorMode !== "part" && editorMode !== "part3d" && editorMode !== "uv") {
    throw new Error("项目编辑模式无效。");
  }

  const tool = value.tool;
  if (!EDITOR_TOOLS.includes(tool as (typeof EDITOR_TOOLS)[number])) {
    throw new Error("项目画笔工具无效。");
  }

  const color = value.color;
  if (typeof color !== "string" || !/^#[0-9A-Fa-f]{8}$/.test(color)) {
    throw new Error("项目颜色无效。");
  }

  const recentColors = Array.isArray(value.recentColors)
    ? value.recentColors.filter(
        (entry): entry is RgbaHex =>
          typeof entry === "string" && /^#[0-9A-Fa-f]{8}$/.test(entry),
      )
    : [];

  const brushSize = value.brushSize;
  if (!BRUSH_SIZES.includes(brushSize as (typeof BRUSH_SIZES)[number])) {
    throw new Error("项目笔刷大小无效。");
  }

  const activeLayer = value.activeLayer;
  if (activeLayer !== "inner" && activeLayer !== "outer") {
    throw new Error("项目活动图层无效。");
  }

  const pan = value.pan;
  if (
    !isRecord(pan) ||
    typeof pan.x !== "number" ||
    typeof pan.y !== "number" ||
    !Number.isFinite(pan.x) ||
    !Number.isFinite(pan.y)
  ) {
    throw new Error("项目画布平移无效。");
  }

  const zoom = value.zoom;
  if (typeof zoom !== "number" || !Number.isFinite(zoom)) {
    throw new Error("项目缩放无效。");
  }

  return {
    selectedSurface: {
      part: surface.part as SurfaceRef["part"],
      layer: surface.layer as SkinLayer,
      face: surface.face as SurfaceRef["face"],
    },
    editorMode,
    tool: tool as EditorTool,
    color: color as RgbaHex,
    recentColors: recentColors.slice(0, 8),
    brushSize: brushSize as BrushSize,
    activeLayer,
    showInner: value.showInner !== false,
    showOuter: value.showOuter !== false,
    mirror: Boolean(value.mirror),
    zoom: Math.min(24, Math.max(0.5, zoom)),
    pan: { x: pan.x, y: pan.y },
  };
}

/** Capture the full in-memory project for export. */
export async function captureProjectBundle(
  options: { now?: () => Date } = {},
): Promise<ProjectBundle> {
  const state = useSkinStore.getState();
  if (state.isAiRunning) {
    throw new Error("AI 正在绘制，请先停止后再导出项目。");
  }
  if (state.strokeBase) {
    state.commitStroke();
  }

  const next = useSkinStore.getState();
  const chatState = useChatSessionStore.getState();
  const past = await Promise.all(
    next.history.slice(-MAX_HISTORY_ENTRIES).map(serializeHistoryEntry),
  );
  const future = await Promise.all(
    next.future.slice(0, MAX_HISTORY_ENTRIES).map(serializeHistoryEntry),
  );

  return {
    format: PROJECT_FORMAT,
    version: PROJECT_VERSION,
    exportedAt: (options.now?.() ?? new Date()).toISOString(),
    skin: await serializeSkinDocument(next.document),
    editor: captureEditorState(),
    history: { past, future },
    chat: {
      activeSessionId: chatState.activeSessionId,
      sessions: chatState.sessions.map((session) => ({
        id: session.id,
        title: session.title,
        createdAt: session.createdAt,
        updatedAt: session.updatedAt,
        history: buildChatHistoryDocument(session.messages, {
          imagePolicy: "user",
          now: options.now,
        }),
      })),
    },
  };
}

export function serializeProjectBundle(bundle: ProjectBundle): string {
  return `${JSON.stringify(bundle)}\n`;
}

export function projectFilename(date = new Date()): string {
  const stamp = date.toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return `skinpilot-project-${stamp}.json`;
}

export function downloadProjectBundle(
  bundle: ProjectBundle,
  filename = projectFilename(),
): void {
  const blob = new Blob([serializeProjectBundle(bundle)], {
    type: "application/json;charset=utf-8",
  });
  const href = URL.createObjectURL(blob);
  const anchor = globalThis.document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  anchor.rel = "noopener";
  anchor.hidden = true;
  globalThis.document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 0);
}

export async function parseProjectBundleJson(
  raw: string,
): Promise<ProjectSnapshot> {
  if (raw.length > MAX_PROJECT_BYTES) {
    throw new Error("项目文件过大（上限约 32MB）。");
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error("项目文件不是有效的 JSON。");
  }
  if (!isRecord(parsed)) throw new Error("项目文件格式不正确。");
  if (parsed.format !== PROJECT_FORMAT) {
    throw new Error(
      '不是 SkinPilot 项目文件（缺少 format: "skinpilot-project"）。',
    );
  }
  if (
    parsed.version !== LEGACY_PROJECT_VERSION &&
    parsed.version !== PROJECT_VERSION
  ) {
    throw new Error(`不支持的项目文件版本：${String(parsed.version)}。`);
  }
  if (!isRecord(parsed.skin)) throw new Error("项目缺少 skin 字段。");
  if (!isRecord(parsed.history)) throw new Error("项目缺少 history 字段。");

  const pastRaw = Array.isArray(parsed.history.past) ? parsed.history.past : [];
  const futureRaw = Array.isArray(parsed.history.future)
    ? parsed.history.future
    : [];
  if (
    pastRaw.length > MAX_HISTORY_ENTRIES ||
    futureRaw.length > MAX_HISTORY_ENTRIES
  ) {
    throw new Error(`撤销/重做栈过长（每侧最多 ${MAX_HISTORY_ENTRIES} 条）。`);
  }

  const document = await deserializeSkinDocument(
    parsed.skin as SerializedSkinDocument,
  );
  const editor = parseEditorState(parsed.editor);
  const history = await Promise.all(
    pastRaw.map((entry, index) =>
      deserializeHistoryEntry(entry, `past[${index}] `),
    ),
  );
  const future = await Promise.all(
    futureRaw.map((entry, index) =>
      deserializeHistoryEntry(entry, `future[${index}] `),
    ),
  );

  const chatWorkspace =
    parsed.version === LEGACY_PROJECT_VERSION
      ? parseLegacyProjectChat(parsed.chat, parsed.exportedAt)
      : parseProjectChatWorkspace(parsed.chat);
  const activeChatSession =
    chatWorkspace.sessions.find(
      (session) => session.id === chatWorkspace.activeSessionId,
    ) ?? chatWorkspace.sessions[0];

  return {
    document,
    editor,
    history,
    future,
    chatSessions: chatWorkspace.sessions,
    activeChatSessionId: activeChatSession.id,
    chatMessages: activeChatSession.messages,
    chatImagesWereStripped: chatWorkspace.imagesWereStripped,
  };
}

function parseLegacyProjectChat(
  value: unknown,
  exportedAt: unknown,
): {
  sessions: ChatSessionRecord[];
  activeSessionId: string;
  imagesWereStripped: boolean;
} {
  const imported =
    value == null
      ? {
          messages: [] as ImportedChatMessage[],
          imagesWereStripped: true,
        }
      : parseChatHistoryJson(
          typeof value === "string" ? value : JSON.stringify(value),
        );
  const timestamp =
    typeof exportedAt === "string" && Number.isFinite(Date.parse(exportedAt))
      ? Date.parse(exportedAt)
      : 0;
  const session: ChatSessionRecord = {
    id: "legacy-chat",
    title: deriveChatSessionTitle(imported.messages),
    createdAt: timestamp,
    updatedAt: timestamp,
    messages: imported.messages,
  };
  return {
    sessions: [session],
    activeSessionId: session.id,
    imagesWereStripped: imported.imagesWereStripped,
  };
}

function parseProjectChatWorkspace(value: unknown): {
  sessions: ChatSessionRecord[];
  activeSessionId: string;
  imagesWereStripped: boolean;
} {
  if (!isRecord(value) || !Array.isArray(value.sessions)) {
    throw new Error("项目缺少会话历史。");
  }
  if (value.sessions.length > MAX_CHAT_SESSIONS) {
    throw new Error(`项目会话过多（最多 ${MAX_CHAT_SESSIONS} 条）。`);
  }

  const seenIds = new Set<string>();
  let imagesWereStripped = false;
  const sessions = value.sessions.map((entry, index) => {
    if (!isRecord(entry)) {
      throw new Error(`项目会话 sessions[${index}] 无效。`);
    }
    const id =
      typeof entry.id === "string" && entry.id.trim() ? entry.id : null;
    if (!id || seenIds.has(id)) {
      throw new Error(`项目会话 sessions[${index}] ID 无效或重复。`);
    }
    seenIds.add(id);
    if (
      !isValidTimestamp(entry.createdAt) ||
      !isValidTimestamp(entry.updatedAt) ||
      entry.updatedAt < entry.createdAt
    ) {
      throw new Error(`项目会话 sessions[${index}] 时间无效。`);
    }
    const imported = parseChatHistoryJson(
      typeof entry.history === "string"
        ? entry.history
        : JSON.stringify(entry.history),
    );
    imagesWereStripped ||= imported.imagesWereStripped;
    return {
      id,
      title:
        typeof entry.title === "string" && entry.title.trim()
          ? entry.title.trim().slice(0, 80)
          : deriveChatSessionTitle(imported.messages),
      createdAt: entry.createdAt,
      updatedAt: entry.updatedAt,
      messages: imported.messages,
    };
  });

  const storedImageDataBytes = sessions.reduce(
    (total, session) => total + measureUserImageDataBytes(session.messages),
    0,
  );
  if (storedImageDataBytes > MAX_STORED_IMAGE_DATA_BYTES) {
    throw new Error("项目中的对话图片超过 20MB 存储上限。");
  }

  if (sessions.length === 0) {
    return parseLegacyProjectChat(null, undefined);
  }
  const activeSessionId =
    typeof value.activeSessionId === "string" &&
    sessions.some((session) => session.id === value.activeSessionId)
      ? value.activeSessionId
      : sessions[0].id;

  return { sessions, activeSessionId, imagesWereStripped };
}

export async function readProjectBundleFile(
  file: File,
): Promise<ProjectSnapshot> {
  if (file.size > MAX_PROJECT_BYTES) {
    throw new Error("项目文件过大（上限约 32MB）。");
  }
  return parseProjectBundleJson(await file.text());
}

/** Apply a parsed snapshot into the live app stores. */
export function applyProjectSnapshot(snapshot: ProjectSnapshot): void {
  const state = useSkinStore.getState();
  if (state.isAiRunning) {
    throw new Error("AI 正在绘制，请先停止后再导入项目。");
  }
  if (state.strokeBase) {
    state.commitStroke();
  }

  useSkinStore.setState({
    document: createSkinDocument(
      snapshot.document.pixels,
      snapshot.document.model,
      snapshot.document.revision,
    ),
    selectedSurface: { ...snapshot.editor.selectedSurface },
    editorMode: snapshot.editor.editorMode,
    tool: snapshot.editor.tool,
    color: snapshot.editor.color,
    recentColors: [...snapshot.editor.recentColors],
    brushSize: snapshot.editor.brushSize,
    activeLayer: snapshot.editor.activeLayer,
    showInner: snapshot.editor.showInner,
    showOuter: snapshot.editor.showOuter,
    mirror: snapshot.editor.mirror,
    zoom: snapshot.editor.zoom,
    pan: { ...snapshot.editor.pan },
    history: snapshot.history.map((entry) => ({
      document: createSkinDocument(
        entry.document.pixels,
        entry.document.model,
        entry.document.revision,
      ),
      source: entry.source,
      label: entry.label,
      chatSessionId: entry.chatSessionId,
      rollbackOfChatSessionId: entry.rollbackOfChatSessionId,
    })),
    future: snapshot.future.map((entry) => ({
      document: createSkinDocument(
        entry.document.pixels,
        entry.document.model,
        entry.document.revision,
      ),
      source: entry.source,
      label: entry.label,
      chatSessionId: entry.chatSessionId,
      rollbackOfChatSessionId: entry.rollbackOfChatSessionId,
    })),
    strokeBase: null,
    aiTransactionBase: null,
    aiTransactionSessionId: null,
    isAiRunning: false,
  });

  useChatSessionStore
    .getState()
    .restoreWorkspace(snapshot.chatSessions, snapshot.activeChatSessionId);
}

export async function exportCurrentProject(): Promise<ProjectBundle> {
  const bundle = await captureProjectBundle();
  downloadProjectBundle(bundle);
  return bundle;
}

export async function importProjectFromFile(
  file: File,
): Promise<ProjectSnapshot> {
  const snapshot = await readProjectBundleFile(file);
  applyProjectSnapshot(snapshot);
  return snapshot;
}
